import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RecommendationEngineEndpoint } from "../../../../types/recommendationEngine";
import {
  ENDPOINT_SUBTITLES,
  HOW_THIS_WORKS_STEPS,
  MENTAL_MODEL_STEPS,
  SECTION_LABELS,
  SELECTION_GUIDANCE_BULLETS,
  TOP_OF_PAGE_EXPLAINER,
} from "../copy";
import { engineTokens } from "../theme";

type Props = {
  endpoint: RecommendationEngineEndpoint;
};

const HowThisWorksAccordion = ({ endpoint }: Props) => {
  return (
    <Accordion
      disableGutters
      sx={{
        mb: 2,
        boxShadow: "none",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          How this works — {ENDPOINT_SUBTITLES[endpoint]}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            fontStyle: "italic",
            borderLeft: `3px solid ${engineTokens.brand}`,
            pl: 1.5,
          }}
        >
          {TOP_OF_PAGE_EXPLAINER}
        </Typography>

        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Mental model
        </Typography>
        <List dense disablePadding sx={{ pl: 2, listStyleType: "disc" }}>
          {MENTAL_MODEL_STEPS.map((step) => (
            <ListItem
              key={step}
              sx={{ display: "list-item", py: 0.25, pl: 0 }}
            >
              <ListItemText
                primary={step}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          ))}
        </List>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Step by step
        </Typography>
        <List dense disablePadding sx={{ pl: 2, listStyleType: "decimal" }}>
          {HOW_THIS_WORKS_STEPS.map((step) => (
            <ListItem
              key={step}
              sx={{ display: "list-item", py: 0.25, pl: 0 }}
            >
              <ListItemText
                primary={step}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          What each section means
        </Typography>
        <Box>
          {Object.entries(SECTION_LABELS).map(([label, description]) => (
            <Box key={label} sx={{ mb: 0.5 }}>
              <Typography
                variant="body2"
                component="span"
                sx={{ fontWeight: 600 }}
              >
                {label}:
              </Typography>{" "}
              <Typography variant="body2" component="span" color="text.secondary">
                {description}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Tuning guidance
        </Typography>
        <List dense disablePadding sx={{ pl: 2, listStyleType: "disc" }}>
          {SELECTION_GUIDANCE_BULLETS.map((bullet) => (
            <ListItem
              key={bullet}
              sx={{ display: "list-item", py: 0.25, pl: 0 }}
            >
              <ListItemText
                primary={bullet}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

export default HowThisWorksAccordion;
